import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    LayoutDashboard, CheckSquare, PlusCircle, ArrowRight,
    StickyNote, CalendarDays, Trash2, Edit2, Save, X,
    BookOpen, Tv, Film, Quote, AlertTriangle
} from "lucide-react";
import "./Dashboard.css";

// --- Componente isolado do Relógio (evita re-render global) ---
function Clock() {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    return (
        <div className="widget-card clock-widget">
            <h2>{time.toLocaleTimeString("pt-BR")}</h2>
            <p>
                {time.toLocaleDateString("pt-BR", {
                    weekday: "long", day: "numeric", month: "long"
                })}
            </p>
        </div>
    );
}

export default function Dashboard() {
    const userName = localStorage.getItem("myWorkspaceName") || "Visitante";

    const [stats, setStats] = useState({
        books: 0, pendingTasks: 0, notes: 0, watchingSeries: 0, movies: 0
    });
    const [recentTasks, setRecentTasks] = useState([]);

    // --- AGENDA (chave unificada com Agenda.jsx) ---
    const [events, setEvents] = useState(() => {
        const saved = localStorage.getItem("myWorkspaceAgenda");
        if (!saved) return [];
        try {
            const parsed = JSON.parse(saved);
            return parsed.map(ev => {
                if (ev.start && ev.end) {
                    return { ...ev, start: new Date(ev.start), end: new Date(ev.end) };
                }
                if (ev.date) {
                    return {
                        id: ev.id,
                        title: ev.title,
                        start: new Date(ev.date + "T09:00:00"),
                        end: new Date(ev.date + "T10:00:00"),
                        type: "work"
                    };
                }
                return ev;
            });
        } catch {
            return [];
        }
    });

    const [newEvent, setNewEvent] = useState("");
    const [newDate, setNewDate] = useState("");
    const [editingId, setEditingId] = useState(null);

    const [itemToDelete, setItemToDelete] = useState(null);

    // --- CARREGA ESTATÍSTICAS (com listener de foco/storage e busca de filmes na API) ---
    useEffect(() => {
        const loadStats = async () => {
            try {
                const books = JSON.parse(localStorage.getItem("myWorkspaceBooks") || "[]").length;
                const notes = JSON.parse(localStorage.getItem("myWorkspaceNotes") || "[]").length;

                const seriesData = JSON.parse(localStorage.getItem("myWorkspaceSeries") || "[]");
                const watching = seriesData.filter(s => s.userStatus === "Assistindo").length;

                const tasksData = JSON.parse(localStorage.getItem("myWorkspaceTasksKanban") || "[]");
                const pending = tasksData.filter(t => t.status !== "done").length;
                const recent = tasksData.filter(t => t.status !== "done").slice(0, 3);

                // Busca filmes cadastrados no backend
                let moviesCount = 0;
                try {
                    const response = await axios.get("http://localhost:5000/api/filmes");
                    moviesCount = Array.isArray(response.data) ? response.data.length : 0;
                } catch (err) {
                    console.error("Erro ao buscar contagem de filmes:", err);
                }

                setStats({
                    books,
                    pendingTasks: pending,
                    notes,
                    watchingSeries: watching,
                    movies: moviesCount
                });
                setRecentTasks(recent);
            } catch (error) {
                console.error("Erro ao carregar estatísticas:", error);
            }
        };

        loadStats();

        const handleFocus = () => loadStats();
        const handleStorage = (e) => {
            if (e.key?.startsWith("myWorkspace")) loadStats();
        };

        window.addEventListener("focus", handleFocus);
        window.addEventListener("storage", handleStorage);
        return () => {
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("storage", handleStorage);
        };
    }, []);

    // --- PERSISTÊNCIA DA AGENDA (chave unificada) ---
    useEffect(() => {
        localStorage.setItem("myWorkspaceAgenda", JSON.stringify(events));
    }, [events]);

    // --- QUOTE DO DIA ---
    const [quote, setQuote] = useState({ text: "", author: "" });
    useEffect(() => {
        const quotesList = [
            { text: "A melhor maneira de prever o futuro é criá-lo.", author: "Peter Drucker" },
            { text: "O sucesso é a soma de pequenos esforços repetidos dia após dia.", author: "Robert Collier" },
            { text: "Não espere. O tempo nunca será o ideal.", author: "Napoleon Hill" },
            { text: "A disciplina é a mãe do sucesso.", author: "Ésquilo" },
            { text: "Comece onde você está. Use o que você tem. Faça o que puder.", author: "Arthur Ashe" },
            { text: "Programação é a arte de dizer a outro humano o que o computador deve fazer.", author: "Donald Knuth" },
            { text: "A simplicidade é o grau máximo de sofisticação.", author: "Leonardo da Vinci" },
            { text: "Você não precisa ser ótimo para começar, mas precisa começar para ser ótimo.", author: "Zig Ziglar" }
        ];
        setQuote(quotesList[Math.floor(Math.random() * quotesList.length)]);
    }, []);

    // --- HANDLERS DA AGENDA ---
    const handleSaveEvent = (e) => {
        e.preventDefault();
        if (!newEvent || !newDate) return;

        const start = new Date(newDate + "T09:00:00");
        const end = new Date(newDate + "T10:00:00");

        let updatedList;
        if (editingId) {
            updatedList = events.map(ev =>
                ev.id === editingId ? { ...ev, title: newEvent, start, end } : ev
            );
            setEditingId(null);
        } else {
            updatedList = [
                ...events,
                { id: Date.now(), title: newEvent, start, end, type: "work" }
            ];
        }

        updatedList.sort((a, b) => new Date(a.start) - new Date(b.start));
        setEvents(updatedList);
        setNewEvent("");
        setNewDate("");
    };

    const startEditing = (event) => {
        setEditingId(event.id);
        setNewEvent(event.title);
        const d = new Date(event.start);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        setNewDate(iso);
    };

    const cancelEditing = () => {
        setEditingId(null);
        setNewEvent("");
        setNewDate("");
    };

    const requestDelete = (id) => setItemToDelete(id);

    const confirmDelete = () => {
        if (itemToDelete) {
            setEvents(events.filter(e => e.id !== itemToDelete));
            if (editingId === itemToDelete) cancelEditing();
            setItemToDelete(null);
        }
    };

    const displayDate = (event) => {
        const d = event.start ? new Date(event.start) : null;
        if (!d || isNaN(d)) return "--/--";
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        return `${day}/${month}`;
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Bom dia";
        if (hour < 18) return "Boa tarde";
        return "Boa noite";
    };

    const summaryData = useMemo(() => [
        { title: "Livros na Estante", value: stats.books, color: "#38bdf8", link: "/biblioteca" },
        { title: "Tarefas Pendentes", value: stats.pendingTasks, color: "#4ade80", link: "/tarefas" },
        { title: "Anotações", value: stats.notes, color: "#c084fc", link: "/anotacoes" },
        { title: "Séries Assistindo", value: stats.watchingSeries, color: "#f472b6", link: "/series" },
        { title: "Filmes na Lista", value: stats.movies, color: "#facc15", link: "/filmes" }
    ], [stats]);

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <div>
                    <h1>{getGreeting()}, {userName}! 👋</h1>
                    <p>Aqui está o panorama do seu ViniHub hoje.</p>
                </div>
            </header>

            {/* --- WIDGETS TOPO --- */}
            <div className="widgets-grid">
                <Clock />

                <div className="widget-card quote-widget">
                    <Quote size={32} className="quote-icon" />
                    <p className="quote-text">"{quote.text}"</p>
                    <div className="quote-author-line"></div>
                    <span className="quote-author">{quote.author}</span>
                </div>
            </div>

            {/* --- CARDS DE ESTATÍSTICAS --- */}
            <div className="dashboard-grid">
                {summaryData.map((item, index) => (
                    <Link key={index} to={item.link} className="card" style={{ textDecoration: "none" }}>
                        <h3>{item.title}</h3>
                        <div
                            className="number"
                            style={{ color: item.color, textShadow: `0 0 10px ${item.color}` }}
                        >
                            {item.value}
                        </div>
                    </Link>
                ))}
            </div>

            {/* --- ÁREA INFERIOR --- */}
            <div className="dashboard-content">
                <div className="left-column-stack">
                    {/* Box de Tarefas Recentes */}
                    <div className="section-box">
                        <h2><CheckSquare size={20} /> Próximas Tarefas</h2>
                        <div className="mini-task-list">
                            {recentTasks.length === 0 ? (
                                <p style={{ color: "var(--text-muted)", fontStyle: "italic", padding: "10px 0" }}>
                                    Nenhuma tarefa pendente.
                                </p>
                            ) : (
                                recentTasks.map(task => (
                                    <div key={task.id} className="mini-task-item">
                                        <div style={{
                                            width: "8px", height: "8px", borderRadius: "50%",
                                            background: "#e78284", flexShrink: 0
                                        }}></div>
                                        <span>{task.title}</span>
                                    </div>
                                ))
                            )}
                        </div>
                        <Link
                            to="/tarefas"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                marginTop: "15px", fontSize: "0.9rem", color: "var(--primary)",
                                fontWeight: "bold", textDecoration: "none"
                            }}
                        >
                            Ver todas as tarefas <ArrowRight size={16} />
                        </Link>
                    </div>

                    {/* Box da Agenda */}
                    <div className={`section-box native-agenda-box ${editingId ? "editing-mode" : ""}`}>
                        <div className="agenda-header" style={{ marginBottom: "15px" }}>
                            <h2>
                                <CalendarDays size={20} />{" "}
                                {editingId ? "Editando Evento" : "Minha Agenda"}
                            </h2>
                        </div>

                        <form onSubmit={handleSaveEvent} className="mini-event-form">
                            <input
                                type="text"
                                placeholder="Novo evento..."
                                value={newEvent}
                                onChange={(e) => setNewEvent(e.target.value)}
                                required
                            />
                            <input
                                type="date"
                                value={newDate}
                                onChange={(e) => setNewDate(e.target.value)}
                                min={new Date().toISOString().split("T")[0]}
                                required
                            />
                            <button type="submit" title={editingId ? "Salvar" : "Adicionar"}>
                                {editingId ? <Save size={18} /> : <PlusCircle size={18} />}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={cancelEditing}
                                    className="btn-cancel"
                                    title="Cancelar"
                                    style={{ marginLeft: "5px" }}
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </form>

                        <div className="events-list">
                            {events.length === 0 && <p className="no-events">Sem eventos futuros.</p>}
                            {events.map(event => (
                                <div
                                    key={event.id}
                                    className={`event-row ${editingId === event.id ? "being-edited" : ""}`}
                                >
                                    <div className="event-date">{displayDate(event)}</div>
                                    <div className="event-title">{event.title}</div>
                                    <div className="event-actions">
                                        <button
                                            onClick={() => startEditing(event)}
                                            className="btn-icon btn-edit"
                                            title="Editar"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => requestDelete(event.id)}
                                            className="btn-icon btn-del"
                                            title="Excluir"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Coluna Direita: Acesso Rápido */}
                <div className="section-box">
                    <h2><LayoutDashboard size={20} /> Acesso Rápido</h2>
                    <div className="shortcuts-grid">
                        <Link to="/tarefas" className="shortcut-btn">
                            <PlusCircle size={20} color="#a6e3a1" />
                            <span>Nova Tarefa</span>
                        </Link>
                        <Link to="/anotacoes" className="shortcut-btn">
                            <StickyNote size={20} color="#f9e2af" />
                            <span>Criar Nota</span>
                        </Link>
                        <Link to="/biblioteca" className="shortcut-btn">
                            <BookOpen size={20} color="#38bdf8" />
                            <span>Add Livro</span>
                        </Link>
                        <Link to="/series" className="shortcut-btn">
                            <Tv size={20} color="#c084fc" />
                            <span>Add Série</span>
                        </Link>
                        <Link to="/filmes" className="shortcut-btn">
                            <Film size={20} color="#facc15" />
                            <span>Add Filme</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* --- MODAL DE CONFIRMAÇÃO --- */}
            {itemToDelete && (
                <div className="dashboard-modal-overlay" onClick={() => setItemToDelete(null)}>
                    <div className="dashboard-modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="dashboard-modal-icon">
                            <AlertTriangle size={48} />
                        </div>
                        <h3>Remover Evento?</h3>
                        <p>Tem certeza que deseja remover este evento da agenda?</p>
                        <div className="dashboard-modal-actions">
                            <button
                                className="dashboard-btn-cancel"
                                onClick={() => setItemToDelete(null)}
                            >
                                Cancelar
                            </button>
                            <button className="dashboard-btn-confirm" onClick={confirmDelete}>
                                Sim, Remover
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}